import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, Loader2, UserCheck } from "lucide-react";
import { AuthLayout, Field } from "@/components/auth/AuthLayout";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

export default function AcceptInvite() {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const { refresh } = useAuth();
    const navigate = useNavigate();
    const [full_name, setFullName] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErr(null);
        setLoading(true);
        try {
            await api.post("/auth/accept-invite", { token, password, full_name });
            await refresh();
            navigate("/", { replace: true });
        } catch (e: any) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout title={t("auth.invite.title")} backHref="/login" backLabelKey="auth.back_to_login">
            <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
                {!token && <div className="text-sm" style={{ color: "var(--error-fg)" }}>{t("auth.invite.no_token")}</div>}
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>{t("auth.invite.desc")}</p>
                <Field label={t("auth.full_name")}>
                    <input data-testid="invite-name-input" required value={full_name} onChange={(e) => setFullName(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
                </Field>
                <Field label={t("auth.password")}>
                    <input data-testid="invite-password-input" required type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
                </Field>
                {err && (
                    <div className="flex items-start gap-2 rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="invite-error">
                        <AlertCircle size={16} className="mt-0.5" />
                        <span>{err}</span>
                    </div>
                )}
                <button data-testid="invite-submit" type="submit" disabled={loading || !token} className="w-full inline-flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <UserCheck size={16} />}
                    {t("auth.invite.submit")}
                </button>
            </form>
        </AuthLayout>
    );
}
